import type * as THREE from 'three';

import {
  BRAND,
  drawLines,
  font,
  makeCanvas,
  makeTexture,
  roundRect,
} from './canvas';

function icon(
  ctx: CanvasRenderingContext2D,
  kind: 'search' | 'target' | 'bolt' | 'stack' | 'check',
  x: number,
  y: number,
  r: number,
  color: string,
): void {
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = Math.max(3, r * 0.14);
  ctx.lineCap = 'round';
  ctx.beginPath();
  switch (kind) {
    case 'search':
      ctx.arc(x - r * 0.15, y - r * 0.15, r * 0.6, 0, Math.PI * 2);
      ctx.moveTo(x + r * 0.3, y + r * 0.3);
      ctx.lineTo(x + r * 0.85, y + r * 0.85);
      ctx.stroke();
      break;
    case 'target':
      ctx.arc(x, y, r * 0.85, 0, Math.PI * 2);
      ctx.moveTo(x + r * 0.45, y);
      ctx.arc(x, y, r * 0.45, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, r * 0.12, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'bolt':
      ctx.moveTo(x + r * 0.25, y - r * 0.9);
      ctx.lineTo(x - r * 0.45, y + r * 0.15);
      ctx.lineTo(x + r * 0.05, y + r * 0.15);
      ctx.lineTo(x - r * 0.25, y + r * 0.9);
      ctx.lineTo(x + r * 0.45, y - r * 0.15);
      ctx.lineTo(x - r * 0.05, y - r * 0.15);
      ctx.closePath();
      ctx.fill();
      break;
    case 'stack':
      for (let i = -1; i <= 1; i++) {
        roundRect(
          ctx,
          x - r * 0.8,
          y + i * r * 0.55 - r * 0.2,
          r * 1.6,
          r * 0.4,
          r * 0.08,
        );
        ctx.stroke();
      }
      break;
    case 'check':
      ctx.arc(x, y, r * 0.85, 0, Math.PI * 2);
      ctx.moveTo(x - r * 0.4, y);
      ctx.lineTo(x - r * 0.1, y + r * 0.3);
      ctx.lineTo(x + r * 0.45, y - r * 0.35);
      ctx.stroke();
      break;
  }
}

/** "WHAT ARE YOU LOOKING FOR?" with three benefit rows, as on the rendering's left wall. */
export function makeLeftPanel(): THREE.CanvasTexture {
  const ctx = makeCanvas(700, 1400);
  ctx.fillStyle = BRAND.white;
  ctx.fillRect(0, 0, 700, 1400);
  drawLines(
    ctx,
    [
      { text: 'WHAT', color: BRAND.navy, font: font('display', 96) },
      { text: 'ARE YOU', color: BRAND.navy, font: font('display', 96) },
      { text: 'LOOKING', color: BRAND.red, font: font('display', 96) },
      { text: 'FOR?', color: BRAND.red, font: font('display', 96) },
    ],
    70,
    150,
    104,
  );
  const rows = [
    ['search', 'POWERFUL SEARCH'],
    ['target', 'REAL RESULTS'],
    ['bolt', 'REAL TIME'],
  ] as const;
  let y = 740;
  for (const [kind, label] of rows) {
    icon(ctx, kind, 120, y, 36, BRAND.navy);
    drawLines(
      ctx,
      [{ text: label, color: BRAND.navy, font: font('body', 30) }],
      200,
      y - 16,
      0,
    );
    y += 150;
  }
  return makeTexture(ctx);
}

/** The navy return-wall panel: "ONE SEARCH. EVERY DISTRIBUTOR. REAL RESULTS." */
export function makeRightPanel(): THREE.CanvasTexture {
  const ctx = makeCanvas(600, 1500);
  ctx.fillStyle = BRAND.navy;
  ctx.fillRect(0, 0, 600, 1500);
  drawLines(
    ctx,
    [
      { text: 'ONE SEARCH.', color: BRAND.white, font: font('display', 44) },
      {
        text: 'EVERY DISTRIBUTOR.',
        color: BRAND.white,
        font: font('display', 44),
      },
      { text: 'REAL RESULTS.', color: BRAND.red, font: font('display', 44) },
    ],
    70,
    220,
    64,
  );
  const chain = ['search', 'stack', 'check'] as const;
  let y = 560;
  for (const [i, kind] of chain.entries()) {
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(300, y, 70, 0, Math.PI * 2);
    ctx.stroke();
    icon(ctx, kind, 300, y, 34, BRAND.white);
    if (i < chain.length - 1) {
      ctx.setLineDash([8, 12]);
      ctx.beginPath();
      ctx.moveTo(300, y + 80);
      ctx.lineTo(300, y + 170);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    y += 250;
  }
  return makeTexture(ctx);
}

/** "FIND IT. FASTER." for the back wall header. */
export function makeHeadline(): THREE.CanvasTexture {
  // Transparent background so it can sit flush beside the logo on the wall.
  const ctx = makeCanvas(1400, 240);
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.font = font('display', 150);
  ctx.fillStyle = BRAND.navy;
  ctx.fillText('FIND IT.', 60, 124);
  const w = ctx.measureText('FIND IT. ').width;
  ctx.fillStyle = BRAND.red;
  ctx.fillText('FASTER.', 60 + w, 124);
  return makeTexture(ctx);
}

/** The kiosk's angled sign. */
export function makeKioskSign(): THREE.CanvasTexture {
  const ctx = makeCanvas(800, 420);
  ctx.fillStyle = BRAND.white;
  ctx.fillRect(0, 0, 800, 420);
  drawLines(
    ctx,
    [
      { text: 'TRY IT LIVE', color: BRAND.navy, font: font('display', 92) },
      { text: 'SEARCH ANYTHING', color: BRAND.red, font: font('body', 48) },
    ],
    400,
    110,
    120,
    'center',
  );
  return makeTexture(ctx);
}

/** The counter-top card: "START YOUR 7-DAY FREE TRIAL" with a QR placeholder. */
export function makeTrialCard(): THREE.CanvasTexture {
  const ctx = makeCanvas(600, 760);
  ctx.fillStyle = BRAND.white;
  ctx.fillRect(0, 0, 600, 760);
  drawLines(
    ctx,
    [
      { text: 'START YOUR', color: BRAND.navy, font: font('display', 54) },
      { text: '7-DAY', color: BRAND.navy, font: font('display', 70) },
      { text: 'FREE TRIAL', color: BRAND.red, font: font('display', 54) },
    ],
    300,
    60,
    78,
    'center',
  );
  // A QR-looking block; the real code goes on the printed card.
  const cells = 21;
  const size = 300;
  const cell = size / cells;
  let seed = 11;
  for (let r = 0; r < cells; r++) {
    for (let c = 0; c < cells; c++) {
      seed = (seed * 48271) % 2147483647;
      const finder =
        (r < 7 && c < 7) ||
        (r < 7 && c >= cells - 7) ||
        (r >= cells - 7 && c < 7);
      const on = finder
        ? r === 0 ||
          r === 6 ||
          c === 0 ||
          c === 6 ||
          (r >= 2 && r <= 4 && c >= 2 && c <= 4) ||
          ((r === 0 || r === 6 || c === cells - 7 || c === cells - 1) &&
            c >= cells - 7) ||
          (r >= 2 && r <= 4 && c >= cells - 5 && c <= cells - 3) ||
          (r >= cells - 7 &&
            (r === cells - 7 || r === cells - 1 || c === 0 || c === 6)) ||
          (r >= cells - 5 && r <= cells - 3 && c >= 2 && c <= 4)
        : seed % 7 < 3;
      if (on)
        ctx.fillRect(150 + c * cell, 330 + r * cell, cell + 0.5, cell + 0.5);
    }
  }
  return makeTexture(ctx);
}
